<template>
    <div class="news-feed-container">
        <!-- 主要内容区域 -->
        <div class="main-content">
            <!-- 左侧用户选择 -->
            <UserListSidebar :selected-user="selectedUser" title="用户列表" :show-post-time-status="true"
                @select-user="selectUser" />

            <!-- 右侧内容区域 -->
            <div class="content-area">
                <!-- 标签页和创建按钮 -->
                <div class="content-tabs">
                    <div class="tabs-left">
                        <div v-for="tab in tabs" :key="tab.key" :class="['tab-item', { active: activeTab === tab.key }]"
                            @click="handleTabChange(tab.key)">
                            <span class="tab-label">{{ tab.label }}</span>
                        </div>
                    </div>
                    <div class="tabs-right">
                        <button class="btn btn-primary create-btn" @click="showCreateModal = true">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                            </svg>
                            <span>创建帖子</span>
                        </button>
                    </div>
                </div>

                <!-- 帖子内容区域 -->
                <div class="post-content">
                    <!-- 加载状态 -->
                    <div v-if="loading" class="loading-container">
                        <div class="loading-spinner"></div>
                        <p>加载中...</p>
                    </div>

                    <!-- 帖子列表 -->
                    <div v-else-if="postsList.length > 0" class="posts-list">
                        <div v-for="post in postsList" :key="post.id" class="post-card">
                            <!-- 帖子头部信息 -->
                            <div class="post-header">
                                <div class="post-info">
                                    <div :class="['status-badge', getStatusClass(post.status)]">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                        </svg>
                                        <span>{{ getStatusText(post.status) }}</span>
                                    </div>

                                    <div class="post-meta">
                                        <div class="meta-item">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                                <path
                                                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                            </svg>
                                            <span>{{ formatDate(post.dateCreated) }}</span>
                                        </div>
                                        <div class="meta-item">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                                <path
                                                    d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z" />
                                            </svg>
                                            <span>#{{ post.id }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="action-buttons">
                                    <button v-if="post.status === 'approved' || post.status === 'approved_by_ai'" class="btn btn-primary"
                                        @click="sendPost(post.id)">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                        </svg>
                                        <span>发布</span>
                                    </button>

                                    <!-- 时间标识 - 移到重新发送按钮左边 -->
                                    <div v-if="post.status === 'sent' && isPostOld(post.dateCreated)"
                                        class="time-indicator">
                                        <div :class="['time-badge', getTimeStatusClass(post.dateCreated)]">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                                <path
                                                    d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                                            </svg>
                                            <span>{{ getTimeStatusText(post.dateCreated) }}</span>
                                        </div>
                                    </div>

                                    <button v-if="post.status === 'sent' && isPostOld(post.dateCreated)"
                                        :class="['btn', 'btn-warning']" @click="resendPost(post.idPost)">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <path
                                                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                        </svg>
                                        <span>重新发送</span>
                                    </button>
                                    <button class="btn btn-danger" @click="deletePost(post.id, post.status)">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <path
                                                d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                        </svg>
                                        <span>删除</span>
                                    </button>
                                </div>
                            </div>

                            <!-- 帖子内容 -->
                            <div class="post-body">
                                <div class="post-text" v-if="post.content?.text">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"
                                        class="quote-icon">
                                        <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
                                    </svg>
                                    <span>{{ post.content?.text }}</span>
                                </div>
                                <div v-if="getPostImages(post).length > 0" class="post-images">
                                    <div v-for="(image, index) in getPostImages(post)" :key="index"
                                        class="image-container">
                                        <ImagePreview :src="image" :alt="`帖子图片 ${index + 1}`"
                                            :preview-src-list="getPostImages(post)" :current-index="index"
                                            :show-preview="true" />
                                    </div>
                                </div>

                                <!-- 用户信息 -->
                                <!-- <div class="post-author">
                                    <div class="author-avatar">
                                        <img :src="getUserAvatar(post.idUser)" :alt="getUserName(post.idUser)" />
                                    </div>
                                    <div class="author-name">{{ getUserName(post.idUser) }}</div>
                                </div> -->
                            </div>
                        </div>
                    </div>

                    <!-- 空状态 -->
                    <div v-else class="empty-state">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                            <path
                                d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                        </svg>
                        <p>暂无帖子数据</p>
                    </div>

                    <!-- 滚动加载更多 -->
                    <div v-if="postsList.length > 0" class="scroll-load-container">
                        <div v-if="loadingMore" class="loading-more">
                            <div class="loading-spinner small"></div>
                            <span>加载中...</span>
                        </div>
                        <div v-else-if="!hasMore" class="no-more">
                            <span>没有更多数据了</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 创建帖子弹窗组件 -->
        <CreatePostModal :visible="showCreateModal" @close="showCreateModal = false" @submit="handleSubmitPost"
            @preview="handlePreviewPost" @error="handleError" @success="handleCreateSuccess" />

        <!-- 消息提示 -->
        <div v-if="errorMessage" :class="['message-toast', messageType]" @click="errorMessage = ''">
            <svg v-if="messageType === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-1.5-1.5L14.5 9 13 7.5 11 9.5l-1.5-1.5L8 9.5 10 11.5l-1.5 1.5L8 15zm2-8h2v2h-2V7zm0 4h2v6h-2v-6z" />
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            <span>{{ errorMessage }}</span>
        </div>
    </div>
</template>

<script>
import CreatePostModal from './components/CreatePostModal.vue'
import ImagePreview from '@/components/imagePreview.vue'
import { getNewsFeedList, deleteNewsFeedPost, resendNewsFeedPost, sendNewsFeedPost, getNewsFeedCandidateList, getUserLastPostTimes } from '@/api/talkytimes/newsFeed'
import UserListSidebar from '@/components/UserListSidebar.vue';

export default {
    name: 'newsFeed',
    components: {
        CreatePostModal,
        ImagePreview,
        UserListSidebar
    },
    data() {
        return {
            activeTab: 'approved',
            showCreateModal: false,
            selectedUser: null, // 初始化为 null，让 watch 来处理用户选择
            errorMessage: '',
            messageType: 'error', // 'error' 或 'success'
            postsList: [], // 帖子列表数据
            loading: false, // 加载状态
            loadingMore: false, // 加载更多状态
            hasMore: true, // 是否有更多数据
            lastId: 0, // 最后一条帖子的ID
            tabs: [
                { key: 'moderation', label: '审核中', statuses: ['on_moderation', 'on_remoderation'] },
                { key: 'approved', label: '已通过', statuses: ['approved', 'approved_by_ai'] },
                { key: 'declined', label: '已拒绝', statuses: ['declined', 'declined_by_ai'] },
                { key: 'sent', label: '已发送', statuses: 'sent' }
            ]
            // 移除 users 数组，改为使用 UserListSidebar 组件的数据
        }
    },
    activated() {
        console.log('activated')
        // 获取用户最新帖子时间并更新用户列表
        this.updateUserLastPostTimes()
    },
    mounted() {
        // 组件已挂载，可以在这里进行其他初始化操作
        this.initData()
        // 添加滚动监听 - 使用nextTick确保DOM已渲染
        this.$nextTick(() => {
            this.addScrollListener()
        })
    },
    computed: {
        // 获取用户列表，与 UserListSidebar 组件使用相同的数据源
        userList() {
            return this.$store.state.accountList.accountList || [];
        }
    },
    watch: {
        // 监听用户列表变化，当有数据时自动选择第一个用户
        userList: {
            handler(newUserList) {
                console.log('userList changed:', newUserList);
                if (newUserList.length > 0 && !this.selectedUser) {
                    console.log('Auto selecting first user:', newUserList[0]);
                    this.selectUser(newUserList[0]);
                }
            },
            immediate: true
        }
    },
    beforeDestroy() {
        // 移除滚动监听
        this.removeScrollListener()
        // 清理定时器
        if (this.scrollTimer) {
            clearTimeout(this.scrollTimer);
        }
    },
    methods: {
        async initData() {
            // 移除重复的数据获取，UserListSidebar 组件会自动获取用户列表
            // 通过 watch 监听 userList 变化来自动选择第一个用户
            console.log('initData - 用户列表将由 UserListSidebar 组件自动获取');
        },
        getNewsFeedListFun(isLoadMore = false) {
            if (isLoadMore) {
                this.loadingMore = true;
            } else {
                this.loading = true;
                this.postsList = []; // 重置列表
                this.lastId = 0; // 重置最后ID
                this.hasMore = true; // 重置是否有更多
            }

            // 根据标签页状态选择不同的接口
            const apiCall = this.activeTab === 'sent' ? getNewsFeedCandidateList : getNewsFeedList;
            
            // 获取当前激活标签页的状态（已发送标签页不需要 statuses 参数）
            const currentTab = this.tabs.find(tab => tab.key === this.activeTab);
            const statuses = currentTab ? currentTab.statuses : ['approved', 'approved_by_ai'];

            const params = {
                limit: 4,
                idLast: this.lastId,
                type: 'post'
            };

            // 只有非已发送标签页才添加 statuses 参数
            if (this.activeTab !== 'sent') {
                params.statuses = statuses;
            }

            console.log('请求参数:', params);

            apiCall(params).then(res => {
                console.log('API响应:', res);
                let newPosts = [];

                // 检查响应数据结构
                if (res && res.data) {
                    newPosts = res.data;
                } else if (Array.isArray(res)) {
                    newPosts = res;
                } else {
                    this.errorMessage = '获取帖子列表失败';
                    return;
                }

                // 如果是从已发送标签页获取的数据，为每个帖子添加status字段
                if (this.activeTab === 'sent') {
                    console.log('newPosts:', newPosts);
                    newPosts = newPosts.map(post => ({
                        ...post,
                        dateCreated: post.date_created,
                        status: 'sent' // 已发送标签页的数据都是已发送状态
                    }));
                }
                console.log('newPosts####::::::::::', newPosts);
                // 处理分页逻辑
                if (isLoadMore) {
                    // 加载更多：追加数据
                    this.postsList = [...this.postsList, ...newPosts];
                } else {
                    // 首次加载：替换数据
                    this.postsList = newPosts;
                }

                // 判断是否有更多数据：如果返回的数据满足4条，则有下一页

                this.hasMore = newPosts.length === 4;
                console.log('newPosts:', newPosts, this.hasMore);
                // 更新最后一条帖子的ID：取当前返回列表中的最后一条数据的id
                if (newPosts.length > 0) {
                    this.lastId = newPosts[newPosts.length - 1].id;
                }

                console.log('帖子列表:', this.postsList);
                console.log('是否有更多:', this.hasMore);
                console.log('最后ID:', this.lastId);

                this.loading = false;
                this.loadingMore = false;
            }).catch(err => {
                console.error('获取帖子列表失败:', err);
                this.loading = false;
                this.loadingMore = false;
                this.errorMessage = '获取帖子列表失败';
                // 如果API失败，使用模拟数据
                if (!isLoadMore) {
                    const currentTab = this.tabs.find(tab => tab.key === this.activeTab);
                    const statuses = currentTab ? currentTab.statuses : ['approved', 'approved_by_ai'];
                    this.postsList = this.getMockData(statuses, this.lastId);
                }
            });
        },
        selectUser(user) {
            localStorage.setItem('activeUser', JSON.stringify(user))
            this.selectedUser = user;
            this.getNewsFeedListFun()
        },
        handleTabChange(tabKey) {
            this.activeTab = tabKey;
            // 切换标签页时重新获取数据
            this.getNewsFeedListFun();
            // 重新绑定滚动监听器
            this.$nextTick(() => {
                this.removeScrollListener();
                this.addScrollListener();
            });
        },
        loadMore() {
            if (this.hasMore && !this.loadingMore) {
                console.log('触发滚动加载更多');
                this.getNewsFeedListFun(true);
            }
        },
        addScrollListener() {
            // 监听滚动事件
            this.scrollHandler = this.handleScroll.bind(this);
            // 监听帖子内容区域的滚动事件
            const postContent = document.querySelector('.post-content');
            if (postContent) {
                postContent.addEventListener('scroll', this.scrollHandler);
                console.log('滚动监听器已绑定到 .post-content');
            } else {
                // 回退到window滚动
                window.addEventListener('scroll', this.scrollHandler);
                console.log('滚动监听器已绑定到 window');
            }
        },
        removeScrollListener() {
            // 移除滚动监听
            if (this.scrollHandler) {
                const postContent = document.querySelector('.post-content');
                if (postContent) {
                    postContent.removeEventListener('scroll', this.scrollHandler);
                } else {
                    window.removeEventListener('scroll', this.scrollHandler);
                }
            }
        },
        handleScroll() {
            // 防抖处理
            if (this.scrollTimer) {
                clearTimeout(this.scrollTimer);
            }

            this.scrollTimer = setTimeout(() => {
                // 检查是否滚动到底部
                const postContent = document.querySelector('.post-content');
                if (postContent) {
                    const scrollTop = postContent.scrollTop;
                    const scrollHeight = postContent.scrollHeight;
                    const clientHeight = postContent.clientHeight;

                    console.log('滚动位置:', {
                        scrollTop,
                        scrollHeight,
                        clientHeight,
                        hasMore: this.hasMore,
                        loadingMore: this.loadingMore,
                        threshold: scrollTop + clientHeight >= scrollHeight - 50
                    });

                    // 当距离底部50px时触发加载更多
                    if (scrollTop + clientHeight >= scrollHeight - 50 && this.hasMore && !this.loadingMore) {
                        console.log('触发加载更多');
                        this.loadMore();
                    }
                } else {
                    // 回退到window滚动
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    const windowHeight = window.innerHeight;
                    const documentHeight = document.documentElement.scrollHeight;

                    if (scrollTop + windowHeight >= documentHeight - 100 && this.hasMore && !this.loadingMore) {
                        this.loadMore();
                    }
                }
            }, 100);
        },
        getTabCount(tabKey) {
            // 根据当前数据返回实际数量
            const tab = this.tabs.find(t => t.key === tabKey);
            if (!tab) return 0;

            // 如果是当前激活的标签页，返回实际数据数量
            if (tabKey === this.activeTab) {
                return this.postsList.length;
            }

            // 对于其他标签页，返回模拟数据（实际项目中应该从API获取）
            const counts = {
                moderation: 5,
                approved: 12,
                declined: 3,
                sent: 8
            };
            return counts[tabKey] || 0;
        },
        // 创建帖子后处理
        handleSubmitPost(postData) {
            // 处理提交帖子逻辑
            console.log('提交帖子:', postData);
            this.showCreateModal = false;
        },
        // 预览帖子图片
        handlePreviewPost(postData) {
            // 处理预览帖子逻辑
            console.log('预览帖子:', postData);
        },
        handleError(message) {
            this.errorMessage = message;
            this.messageType = 'error';
        },
        handleCreateSuccess(response) {
            // 创建成功后刷新列表
            console.log('帖子创建成功:', response);
            // 刷新当前标签页的列表
            this.getNewsFeedListFun();
        },
        getStatusClass(status) {
            const statusMap = {
                'on_moderation': 'moderation',
                'approved': 'ready',
                'declined': 'declined',
                'sent': 'sent'
            };
            return statusMap[status] || 'ready';
        },
        getStatusText(status) {
            const statusMap = {
                'on_moderation': '审核中',
                'approved': '准备发布',
                'declined': '审核拒绝',
                'sent': '已发布'
            };
            return statusMap[status] || '准备发布';
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            }).replace(/\//g, '/');
        },
        getPostImages(post) {
            // 处理不同格式的图片数据
            if (post.content && post.content.images && Array.isArray(post.content.images)) {
                // API返回的格式：content.images[].urlStandart
                return post.content.images.map(img => img.urlStandart || img.urlPreview || img);
            } else if (post.images && Array.isArray(post.images)) {
                // 直接是images数组
                return post.images;
            }
            return [];
        },
        getUserAvatar(userId) {
            // 根据用户ID返回对应的头像
            const accountList = this.$store.state.accountList.accountList || [];
            const user = accountList.find(u => u.id === userId.toString());
            return user ? user.personalAvatar_xl : (accountList[0]?.personalAvatar_xl || '');
        },
        getUserName(userId) {
            // 根据用户ID返回对应的用户名
            const accountList = this.$store.state.accountList.accountList || [];
            const user = accountList.find(u => u.id === userId.toString());
            return user ? user.name : (accountList[0]?.name || '未知用户');
        },
        deletePost(postId, status) {
            this.$confirm('确定要删除这封邮件吗？', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                // 执行删除操作
                console.log('删除帖子:', postId, status);
                // return
                deleteNewsFeedPost({
                    id: postId,
                    status: status
                }).then(res => {
                    console.log('删除帖子:', res);
                    this.getNewsFeedListFun();
                })
            })
        },
        sendPost(postId) {
            console.log('发送帖子:', postId);
            // return;
            sendNewsFeedPost({
                id: postId
            }).then(res => {
                console.log('发送帖子:', res);
                this.getNewsFeedListFun();
            })
        },
        resendPost(postId) {
            console.log('重新发送帖子:', postId);
            // return;
            resendNewsFeedPost({
                id: postId
            }).then(res => {
                console.log('重新发送帖子:', res);
                if (res && res.error === 'daily_sent_post_limit_exceeded') {
                    this.errorMessage = '今日发送帖子数量已达上限，请明天再试';
                    this.messageType = 'error';
                } else {
                    this.getNewsFeedListFun();
                }
            })
        },

        // 检查帖子是否超过12小时
        isPostOld(dateCreated) {
            if (!dateCreated) return false;
            const postDate = new Date(dateCreated);
            const now = new Date();
            const hoursDiff = (now - postDate) / (1000 * 60 * 60);
            return hoursDiff >= 12;
        },

        // 获取时间状态样式类
        getTimeStatusClass(dateCreated) {
            if (!dateCreated) return 'normal';
            const postDate = new Date(dateCreated);
            const now = new Date();
            const hoursDiff = (now - postDate) / (1000 * 60 * 60);

            if (hoursDiff >= 12) {
                return 'old';
            } else if (hoursDiff >= 10) {
                return 'warning';
            } else {
                return 'normal';
            }
        },

        // 获取时间状态文本
        getTimeStatusText(dateCreated) {
            if (!dateCreated) return '';
            const postDate = new Date(dateCreated);
            const now = new Date();
            const hoursDiff = (now - postDate) / (1000 * 60 * 60);

            if (hoursDiff >= 12) {
                return `已发送${Math.floor(hoursDiff)}小时`;
            } else if (hoursDiff >= 10) {
                return `即将过期(${Math.floor(12 - hoursDiff)}小时后)`;
            } else {
                return `已发送${Math.floor(hoursDiff)}小时`;
            }
        },

        // 更新用户最新帖子时间
        async updateUserLastPostTimes() {
            try {
                const response = await getUserLastPostTimes();
                console.log('用户最新帖子时间:', response);

                // 检查响应格式：{ code: 200, msg: "操作成功", data: [...] }
                if (response && response.code === 200 && Array.isArray(response.data)) {
                    const userPostTimes = response.data;

                    // 更新store中的用户数据
                    const accountList = this.$store.state.accountList.accountList || [];
                    const updatedAccountList = accountList.map(user => {
                        const userPostTime = userPostTimes.find(item => item.pdUserId === user.id);
                        if (userPostTime) {
                            return {
                                ...user,
                                lastPostTime: userPostTime.sendTime,
                                isNowSend: userPostTime.isNowSend, // 添加isNowSend字段
                                postTimeStatus: this.getUserPostTimeStatus(userPostTime.isNowSend)
                            };
                        }
                        return user;
                    });

                    // 更新store
                    this.$store.commit('accountList/SET_ACCOUNT_LIST', updatedAccountList);

                    // 移除本地users数组的更新，因为现在直接使用store中的数据
                }
            } catch (error) {
                console.error('获取用户最新帖子时间失败:', error);
            }
        },

        // 获取用户帖子时间状态
        getUserPostTimeStatus(isNowSend) {
            // 使用isNowSend字段判断状态，而不是时间计算
            if (isNowSend === true) {
                return 'normal'; // 已发送，状态正常
            } else if (isNowSend === false) {
                return 'old'; // 未发送，需要发帖
            } else {
                return 'normal'; // 默认状态
            }
        },

        // 获取用户帖子时间状态文本
        getUserPostTimeStatusText(user) {
            if (user && user.sendTime) {
                const now = new Date();
                const sendT = new Date(user.sendTime.replace(/-/g, '/'));
                const hourDiff = Math.floor((now - sendT) / 1000 / 60 / 60);
                if (hourDiff >= 24) {
                    return `超过${hourDiff}小时未发帖`;
                } else {
                    return '';
                }
            } else if (user && user.isNowSend === true) {
                return '已发帖';
            } else {
                return '';
            }
        }
    }
}
</script>

<style scoped>
.news-feed-container {
    width: 100%;
    /* min-height: 100vh; */
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
    display: flex;
    flex-direction: column;
    font-family: 'PingFang SC', 'Microsoft YaHei', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* 顶部导航栏 */
.top-navbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 30px;
    background: rgba(255, 255, 255, 0.95);
    border-bottom: 1px solid rgba(229, 231, 235, 0.8);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    z-index: 100;
}

.nav-left {
    display: flex;
    gap: 15px;
}

.nav-icon {
    color: #6b7280;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.nav-icon:hover {
    color: #8b5cf6;
    background: rgba(139, 92, 246, 0.1);
    transform: translateY(-1px);
}

.nav-icon.active {
    color: #8b5cf6;
    background: rgba(139, 92, 246, 0.1);
}

.nav-center {
    flex: 1;
    display: flex;
    justify-content: center;
}

.connection-status {
    display: flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
    border-radius: 20px;
    padding: 8px 16px;
    font-size: 13px;
    color: #4f46e5;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(79, 70, 229, 0.2);
    animation: pulse 2s infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.8;
    }
}

.status-dot {
    width: 8px;
    height: 8px;
    background: #4f46e5;
    border-radius: 50%;
    animation: blink 1.5s infinite;
}

@keyframes blink {

    0%,
    50% {
        opacity: 1;
    }

    51%,
    100% {
        opacity: 0.3;
    }
}

.nav-right {
    display: flex;
    gap: 15px;
}

/* 主要内容区域 */
.main-content {
    display: flex;
    flex: 1;
    overflow: hidden;
}

/* 左侧用户选择 */
.user-sidebar {
    width: 300px;
    background: rgba(255, 255, 255, 0.8);
    border-right: 1px solid rgba(229, 231, 235, 0.8);
    display: flex;
    flex-direction: column;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
}

.sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 20px;
    border-bottom: 1px solid rgba(229, 231, 235, 0.8);
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.sidebar-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.user-count {
    font-size: 13px;
    color: #6b7280;
    background: rgba(139, 92, 246, 0.1);
    padding: 4px 12px;
    border-radius: 12px;
    font-weight: 500;
}

.user-list {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
}

.user-item {
    display: flex;
    align-items: center;
    padding: 16px;
    margin-bottom: 12px;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid transparent;
    background: rgba(241, 245, 249, 0.8);
    position: relative;
    overflow: hidden;
}

.user-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s;
}

.user-item:hover::before {
    left: 100%;
}

.user-item:hover {
    background: rgba(226, 232, 240, 0.9);
    border-color: rgba(139, 92, 246, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(139, 92, 246, 0.15);
}

.user-item.active {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
    border-color: #8b5cf6;
    box-shadow: 0 8px 25px rgba(139, 92, 246, 0.3);
    transform: translateY(-2px);
}

.user-item.active .user-nickname {
    color: white;
}

.user-item.active .user-id {
    color: rgba(255, 255, 255, 0.8);
}

.user-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    margin-right: 16px;
    border: 3px solid rgba(229, 231, 235, 0.8);
    position: relative;
    transition: all 0.3s ease;
}

.user-item.active .user-avatar {
    border-color: rgba(255, 255, 255, 0.8);
    transform: scale(1.1);
}

.user-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.user-item:hover .user-avatar img {
    transform: scale(1.1);
}

.status-indicator {
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 14px;
    height: 14px;
    background: #10b981;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    animation: pulse 2s infinite;
}

.user-info {
    flex: 1;
}

.user-nickname {
    font-weight: 700;
    font-size: 15px;
    color: #1f2937;
    margin-bottom: 4px;
    transition: color 0.3s ease;
}

.user-id {
    font-size: 13px;
    color: #6b7280;
    font-weight: 500;
    transition: color 0.3s ease;
}

.user-actions {
    display: flex;
    align-items: center;
    margin-left: 15px;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.user-item:hover .user-actions {
    opacity: 1;
}

.action-dot {
    width: 8px;
    height: 8px;
    background: #ef4444;
    border-radius: 50%;
    animation: pulse 1.5s infinite;
}

/* 右侧内容区域 */
.content-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: rgba(255, 255, 255, 0.6);
    /* backdrop-filter: blur(10px); */
    height: calc(100vh - 60px);
    /* 固定高度，减去顶部导航栏高度 */
    overflow: hidden;
    /* 改为hidden，让内部元素控制滚动 */
}

.content-tabs {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(229, 231, 235, 0.8);
    padding: 0 30px;
    background: rgba(255, 255, 255, 0.9);
}

.tabs-left {
    display: flex;
}

.tabs-right {
    display: flex;
    align-items: center;
}

.tab-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 20px 24px;
    cursor: pointer;
    color: #6b7280;
    border-bottom: 3px solid transparent;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 600;
    font-size: 14px;
    position: relative;
}

.tab-item::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 3px;
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    transition: all 0.3s ease;
    transform: translateX(-50%);
}

.tab-item:hover {
    color: #8b5cf6;
    background: rgba(139, 92, 246, 0.05);
}

.tab-item.active {
    color: #8b5cf6;
}

.tab-item.active::after {
    width: 100%;
}

.tab-label {
    font-weight: 700;
}

.tab-count {
    background: rgba(139, 92, 246, 0.1);
    color: #8b5cf6;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    transition: all 0.3s ease;
}

.tab-item.active .tab-count {
    background: rgba(139, 92, 246, 0.2);
    color: #7c3aed;
}

.post-content {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
    height: calc(100vh - 140px);
    /* 固定高度，减去顶部导航栏和标签页高度 */
}

.post-card {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(229, 231, 235, 0.8);
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    /* backdrop-filter: blur(10px); */
    transition: all 0.3s ease;
    /* margin: 0 auto; */
}

.post-card:hover {
    /* transform: translateY(-2px); */
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
}

.post-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(243, 244, 246, 0.8);
}

.post-info {
    flex: 1;
}

.status-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 8px;
    width: fit-content;
}

.status-badge.ready {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1d4ed8;
    box-shadow: 0 2px 6px rgba(29, 78, 216, 0.15);
}

.status-badge.moderation {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #d97706;
    box-shadow: 0 2px 6px rgba(217, 119, 6, 0.15);
}

.status-badge.declined {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    color: #dc2626;
    box-shadow: 0 2px 6px rgba(220, 38, 38, 0.15);
}

.status-badge.sent {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    color: #059669;
    box-shadow: 0 2px 6px rgba(5, 150, 105, 0.15);
}

/* 时间标识样式 */
.time-indicator {
    margin-bottom: 0;
    margin-right: 8px;
}

.time-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    width: fit-content;
}

.time-badge.normal {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1d4ed8;
    box-shadow: 0 2px 4px rgba(29, 78, 216, 0.1);
}

.time-badge.warning {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #d97706;
    box-shadow: 0 2px 4px rgba(217, 119, 6, 0.15);
}

.time-badge.old {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    color: #dc2626;
    box-shadow: 0 2px 4px rgba(220, 38, 38, 0.15);
}

.status-badge svg {
    width: 12px;
    height: 12px;
}

.post-meta {
    display: flex;
    gap: 16px;
    color: #6b7280;
    font-size: 12px;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: rgba(243, 244, 246, 0.6);
    border-radius: 6px;
    transition: all 0.3s ease;
}

.meta-item:hover {
    background: rgba(139, 92, 246, 0.1);
    color: #8b5cf6;
}

.meta-item svg {
    width: 12px;
    height: 12px;
}

.action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
}

.btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
}

.btn:hover::before {
    left: 100%;
}

.btn-primary {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
    box-shadow: 0 3px 10px rgba(139, 92, 246, 0.3);
}

.btn-primary:hover {
    background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
    box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
}

.btn-danger {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    box-shadow: 0 3px 10px rgba(239, 68, 68, 0.3);
}

.btn-danger:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

.btn-warning {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
    box-shadow: 0 3px 10px rgba(245, 158, 11, 0.3);
}

.btn-warning:hover {
    background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
    box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4);
}

.post-body {
    margin-bottom: 0;
}

.post-text {
    color: #1f2937;
    line-height: 1.6;
    margin-bottom: 16px;
    font-size: 14px;
    padding: 16px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 10px;
    border-left: 3px solid #8b5cf6;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.post-text .quote-icon {
    width: 16px;
    height: 16px;
    color: #8b5cf6;
    flex-shrink: 0;
    margin-top: 1px;
}

.post-text span {
    flex: 1;
    word-break: break-word;
    font-weight: 500;
}

.post-images {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.post-author {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(243, 244, 246, 0.8);
}

.author-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid rgba(229, 231, 235, 0.8);
}

.author-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.author-name {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
}

.image-container {
    width: 180px;
    height: 180px;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.image-container:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.create-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 600;
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
    border-radius: 10px;
    box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.create-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
}

.create-btn:hover::before {
    left: 100%;
}

.create-btn:hover {
    background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
    box-shadow: 0 8px 25px rgba(139, 92, 246, 0.4);
}

/* 消息提示 */
.message-toast {
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 15px 25px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
    z-index: 1000;
    cursor: pointer;
    transition: all 0.3s ease-in-out;
    color: white;
    font-weight: 500;
}

.message-toast.error {
    background-color: #ef4444;
    box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
}

.message-toast.success {
    background-color: #10b981;
    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.message-toast:hover {
    opacity: 0.9;
    transform: translateY(-2px);
}

.message-toast svg {
    width: 20px;
    height: 20px;
    color: white;
}

/* 滚动条样式 */
.user-list::-webkit-scrollbar,
.post-content::-webkit-scrollbar {
    width: 8px;
}

.user-list::-webkit-scrollbar-track,
.post-content::-webkit-scrollbar-track {
    background: rgba(241, 245, 249, 0.8);
    border-radius: 4px;
}

.user-list::-webkit-scrollbar-thumb,
.post-content::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.3);
    border-radius: 4px;
    transition: background 0.3s ease;
}

.user-list::-webkit-scrollbar-thumb:hover,
.post-content::-webkit-scrollbar-thumb:hover {
    background: rgba(139, 92, 246, 0.5);
}

/* 加载状态 */
.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #6b7280;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #8b5cf6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* 空状态 */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #9ca3af;
    text-align: center;
}

.empty-state svg {
    margin-bottom: 16px;
    opacity: 0.5;
}

.empty-state p {
    font-size: 16px;
    font-weight: 500;
    margin: 0;
}

/* 帖子列表 */
.posts-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* 滚动加载更多 */
.scroll-load-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    margin-top: 10px;
}

.loading-more {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #6b7280;
    font-size: 14px;
}

.loading-spinner.small {
    width: 20px;
    height: 20px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #8b5cf6;
    margin-bottom: 0;
}

.no-more {
    color: #9ca3af;
    font-size: 14px;
    font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .user-sidebar {
        width: 250px;
    }

    .content-tabs {
        padding: 0 20px;
    }

    .tab-item {
        padding: 16px 16px;
        font-size: 13px;
    }

    .post-content {
        padding: 20px;
    }

    .post-card {
        padding: 24px;
    }
}

@media (max-width: 480px) {
    .top-navbar {
        padding: 12px 20px;
    }

    .nav-left,
    .nav-right {
        gap: 10px;
    }

    .user-sidebar {
        width: 200px;
    }

    .sidebar-header {
        padding: 16px;
    }

    .user-item {
        padding: 12px;
    }

    .user-avatar {
        width: 40px;
        height: 40px;
        margin-right: 12px;
    }

    .post-header {
        flex-direction: column;
        gap: 16px;
    }

    .action-buttons {
        width: 100%;
        justify-content: space-between;
    }

    .post-images {
        flex-direction: column;
    }

    .image-container {
        max-width: 100%;
    }
}
</style>